import {
  ThoughtsAndSpeakUpsResponseInterface,
  thoughtsAndSpeakUpsApiHandler,
  useMarkThoughtResponse,
} from '@api/thoughtsAndSpeakUpsApiHandler';
import EmptyIcon from '@assets/images/earlyWarning/chat-icon-v2.svg';
import SpeakUpChatIcon from '@assets/images/earlyWarning/chat-icon.svg';
import AutocompleteComponent from '@components/Atoms/Autocomplete';
import { useArchiveSpeakUpResponse } from '@hooks/api/useSpeakUp';
import { useAppSelector } from '@hooks/useAppSelector';
import useWindowDimensions from '@hooks/useWindowDimensions';
import {
  FilledTextFieldProps,
  OutlinedTextFieldProps,
  StandardTextFieldProps,
  TextField,
  TextFieldVariants,
} from '@mui/material';
import { MotionImage, MotionText } from '@shared/atoms';
import {
  CreateSpeakUp,
  LoadingBackdrop,
  ShareThoughtAndSpeakUpCard,
  SystemButton,
} from '@shared/molecules';
import { CoreLayout } from '@shared/templates';
import { useIsFetching, useQuery, useQueryClient } from '@tanstack/react-query';
import { CustomAutocompletePopper, CustomPopper, FLIP_POPPER_MODS } from '@utils/mui.util';
import { motion } from 'framer-motion';
import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import { RootState } from 'store';
import { SpeakUpCommentSkeleton } from './LoadingSkeletons';
import styles from './styles.module.scss';

const MuiStyles = {
  autocomplete: {
    width: '100%',
    minWidth: '280px',
    marginTop: '-4px',
    '& .MuiOutlinedInput-root': {
      borderRadius: '40px',
      height: '44px',
      border: '3px solid #019df2',
      '& .MuiInputBase-input': {
        color: '#019df2',
        fontWeight: 600,
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#019df2',
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#019df2',
      },
      '& .MuiAutocomplete-popupIndicator': {
        color: '#019df2',
      },
    },
    '@media (max-width: 600px)': {
      width: '100% !important',
    },
  },
};

export const SharedThoughtsAndSpeakUpsPage: React.FC = () => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const { isDesktop, isTablet } = useWindowDimensions();
  const [filterByTypeLoading, setFilterByTypeLoading] = useState(true);
  const [selectedFilterByType, setSelectedFilterByType] = useState<{
    label: string;
    value: number;
  }>({ value: 1, label: 'All types' });
  const [thoughtsAndSpeakUps, setThoughtsAndSpeakUps] = useState<
    ThoughtsAndSpeakUpsResponseInterface[] | []
  >([]);
  const seenAndArchiveMutation = useArchiveSpeakUpResponse();
  const markThoughtAsSeenMutation = useMarkThoughtResponse();
  const [speakUpOpen, setSpeakUpOpen] = useState(false);
  const companyConfigData = useAppSelector((state: RootState) => state.companyConfig);
  const isThoughtsAndSpeakUpsFetching = useIsFetching({ queryKey: ['thoughtsAndSpeakUps'] }) > 0;

  const {
    data,
    isLoading,
    isSuccess,
    refetch: refetchData,
  } = useQuery({
    queryKey: ['thoughtsAndSpeakUps', selectedFilterByType.value],
    queryFn: () =>
      thoughtsAndSpeakUpsApiHandler.getThoughtsAndSpeakUps({
        archived: null,
        type:
          selectedFilterByType.value === 1
            ? null
            : selectedFilterByType.value === 2
            ? 'thoughts'
            : 'speakUps',
      }),
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });

  const { data: notArchivedData } = useQuery({
    queryKey: ['thoughtsAndSpeakUpsNotArchived'],
    queryFn: () =>
      thoughtsAndSpeakUpsApiHandler.getThoughtsAndSpeakUps({
        archived: false,
        type: null,
      }),
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });

  const idsForProcess = useMemo(() => {
    const hasNotArchivedData = notArchivedData?.success && notArchivedData?.responseObject;

    const notArchivedIds = hasNotArchivedData
      ? notArchivedData?.responseObject.map((item) => ({ id: item.id, type: item.type }))
      : [];

    return notArchivedIds;
  }, [notArchivedData]);

  useEffect(() => {
    if (idsForProcess) {
      processItems();
    }
  }, [idsForProcess]);

  const processItems = async () => {
    const promises = idsForProcess.map(async (item) => {
      if (item.type === 'thoughts') {
        return markThoughtAsSeenMutation.mutateAsync({ thoughtId: item.id });
      }

      if (item.type === 'speakUps') {
        return seenAndArchiveMutation.mutateAsync({ speakUpId: item.id });
      }

      return undefined;
    });

    if (idsForProcess?.length > 0) {
      await Promise.all(promises);
      await queryClient.invalidateQueries({ queryKey: ['thoughtsAndSpeakUpsForNotifications'] });
    }
  };

  useEffect(() => {
    if (data) {
      if (data?.success) {
        setThoughtsAndSpeakUps(data.responseObject);
      } else {
        setThoughtsAndSpeakUps([]);
      }
    }
  }, [data?.responseObject]);

  const filterByTypes = [
    { value: 1, label: 'All types' }, // value 1 = null
    { value: 2, label: 'Shared thoughts' }, // value 2 = speakUps
    { value: 3, label: 'Speak ups' }, // value 3 = thoughts
  ];

  const handleChange = (_event: any, newValue: { value: number; label: string } | null) => {
    setSelectedFilterByType(newValue || filterByTypes[0]);
  };

  const hasData = !isLoading && thoughtsAndSpeakUps?.length > 0;

  const handleSpeakUpClose = (open: boolean) => {
    refetchData();
    setSpeakUpOpen(open);
  };

  return (
    <CoreLayout title="Shared Thoughts & SpeakUp Page" navigationType="default">
      {isLoading && <LoadingBackdrop text="Warming up your voice – your story matters…" />}

      <motion.div
        className={`container ${styles.container}`}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
      >
        <div className={styles.topContainer}>
          <MotionText as="h2" text="Shared Thoughts and Speak ups" />

          {companyConfigData?.data?.showSpeakUp && (
            <SystemButton
              fullWidthButton={!isDesktop}
              className={styles.speakUpBtn}
              variant="outlinedPrimarySkyBlue"
              size="medium"
              onClick={() => setSpeakUpOpen(true)}
            >
              <MotionImage
                src={SpeakUpChatIcon}
                alt=""
                aria-hidden="true"
                className={styles.speakUpIcon}
              />
              Speak Up
            </SystemButton>
          )}
          {/* <div className={styles.navigateBack} onClick={() => history.goBack()}>
            <MotionImage src={SkyBluePrevious} alt="HS Previous" className={styles.previous} />
            <h5>back</h5>
          </div> */}
        </div>

        <div className={styles.filters}>
          <div className={styles.filterBox}>
            <p>Display:</p>

            <div>
              <AutocompleteComponent
                isOptionEqualToValue={(option: { value: any }, value: { value: any }) =>
                  option?.value === value?.value
                }
                loading={filterByTypeLoading}
                includeInputInList
                filterSelectedOptions
                autoHighlight
                onOpen={() => {
                  setFilterByTypeLoading(true);
                  setTimeout(() => {
                    setFilterByTypeLoading(false);
                  }, 1000);
                }}
                disableClearable
                options={filterByTypes}
                getOptionLabel={(option: { label: string }) => option?.label || ''}
                renderInput={(
                  params: JSX.IntrinsicAttributes & {
                    variant?: TextFieldVariants | undefined;
                  } & Omit<
                      FilledTextFieldProps | OutlinedTextFieldProps | StandardTextFieldProps,
                      'variant'
                    >,
                ) => (
                  <TextField
                    {...params}
                    label=""
                    sx={MuiStyles.autocomplete}
                    inputProps={{
                      ...params.inputProps,
                      readOnly: true, // Prevents typing
                    }}
                  />
                )}
                value={selectedFilterByType}
                onChange={handleChange}
                sx={MuiStyles.autocomplete}
                className={styles.autoComplete}
                disablePortal
                PopperComponent={isDesktop ? CustomPopper : CustomAutocompletePopper}
                componentsProps={FLIP_POPPER_MODS}
              />
            </div>
          </div>
        </div>

        <div className={styles.cardsContainer}>
          {isLoading || isThoughtsAndSpeakUpsFetching ? (
            Array.from({ length: 6 }).map((_, index) => <SpeakUpCommentSkeleton key={index} />)
          ) : hasData ? (
            thoughtsAndSpeakUps.map((item, index) => (
              <ShareThoughtAndSpeakUpCard
                key={index}
                id={item.id}
                mainTitle={
                  item.type === 'thoughts'
                    ? `${
                        item?.shared ? 'Shared thoughts with my oganisation' : 'Thoughts for myself'
                      }`
                    : 'Speak up'
                }
                commentDate={item.createdAt || 'N/A'}
                comment={item?.message || 'No details provided'}
                responseDate={item.latestResponse?.createdAt || null}
                response={
                  item.latestResponse?.response ||
                  'Your feedback has been received and awaiting response from your organisation.'
                }
                buttonText={item.type === 'thoughts' ? 'View Response' : 'View & Message'}
                questionnaireName={item?.questionnaire || undefined}
                type={item.type}
                questionLabel={item?.questionLabel || 'No Label'}
                score={item?.score || undefined}
                hideResponseSection={item?.type === 'thoughts' && !item?.shared}
              />
            ))
          ) : (
            <div className={styles.noDataContainer}>
              <MotionImage src={EmptyIcon} alt="" aria-hidden="true" className={styles.emptyIcon} />
              <MotionText as="h4" text="No speak ups or thoughts found" />
              {/* <MotionText
                as="text"
                text="This is your private space to write down thoughts and help you process emotions and gain clarity."
              /> */}
            </div>
          )}
        </div>
      </motion.div>

      {speakUpOpen && (
        <CreateSpeakUp
          createSpeakUpOpen={speakUpOpen}
          handleSpeakUpModalOpen={handleSpeakUpClose}
          assessmentId={null}
          questionnaireId={null}
        />
      )}
    </CoreLayout>
  );
};
